import { Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import APIClient from '../api/api';
import { AuditEventSearchResult } from '../api/models';
import moment from 'moment';

interface AuditEventsModalProps {
    visible: boolean;
    onClose: () => void;
    startDate: Date;
    endDate: Date;
    eventTypes: string[];
    account?: string;
    title: string;
}

const PAGE_SIZE = 10;
const FETCH_PAGE_SIZE = 100;

export const AuditEventsModal: React.FC<AuditEventsModalProps> = ({
    visible,
    onClose,
    startDate,
    endDate,
    eventTypes,
    account,
    title,
}) => {
    const [loading, setLoading] = useState(false);
    const [allData, setAllData] = useState<AuditEventSearchResult[]>([]);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setPage(1);

        const fetchAll = async () => {
            setLoading(true);
            try {
                const all: AuditEventSearchResult[] = [];
                let fetchPage = 1;
                while (true) {
                    const response = await APIClient.searchAuditEvents({
                        startDate,
                        endDate,
                        eventTypes,
                        account,
                        page: fetchPage,
                        pageSize: FETCH_PAGE_SIZE,
                    });
                    all.push(...response.content);
                    if (response.currentPage >= response.totalPages) break;
                    fetchPage++;
                }
                setAllData(all);
            } catch (error) {
                console.error('Failed to fetch audit events:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [visible, startDate, endDate, eventTypes, account]);

    const totalPages = Math.ceil(allData.length / PAGE_SIZE);
    const pageData = allData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Modal
            title={`${title} (${allData.length})`}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
        >
            {loading ? (
                <div className="flex justify-center py-8"><Spin /></div>
            ) : (
                <>
                    <div className="max-h-[60vh] overflow-y-auto">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="text-left text-2xs uppercase tracking-wider text-text-3 font-semibold py-2 px-3">Domain</th>
                                <th className="text-left text-2xs uppercase tracking-wider text-text-3 font-semibold py-2 px-3">Event Type</th>
                                <th className="text-left text-2xs uppercase tracking-wider text-text-3 font-semibold py-2 px-3">Created At</th>
                                <th className="text-left text-2xs uppercase tracking-wider text-text-3 font-semibold py-2 px-3">Event Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageData.map((record) => (
                                <tr key={`${record.domain}-${record.createdAt}`} className="border-t border-border hover:bg-bg-elevated transition-colors">
                                    <td className="py-2 px-3 text-sm text-text">{record.domain}</td>
                                    <td className="py-2 px-3 text-sm text-text-2">{record.auditEventType}</td>
                                    <td className="py-2 px-3 text-sm text-text-2 font-mono tabular-nums">{moment.utc(record.createdAt).format('YYYY-MM-DD HH:mm:ss[Z]')}</td>
                                    <td className="py-2 px-3 text-sm text-text-2">{record.eventSource}</td>
                                </tr>
                            ))}
                            {pageData.length === 0 && (
                                <tr><td colSpan={4} className="py-4 text-center text-sm text-text-3">No events found</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                    {allData.length > PAGE_SIZE && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                            <span className="text-xs text-text-3">{allData.length} events</span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => p - 1)}
                                    className="text-xs text-accent hover:opacity-80 disabled:text-text-3 disabled:cursor-default cursor-pointer"
                                >
                                    Prev
                                </button>
                                <span className="text-xs text-text-2 font-mono tabular-nums">{page} / {totalPages}</span>
                                <button
                                    type="button"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                    className="text-xs text-accent hover:opacity-80 disabled:text-text-3 disabled:cursor-default cursor-pointer"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};
